import { forwardRef, useState } from 'react';
import { useMutation } from 'react-query';
import { BarChartIcon, ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { Alert, AlertDescription } from 'components/ui/alert';
import { ApiErrorAlert } from 'components/ui/api-error-alert';
import { Button } from 'components/ui/button';
import { Spinner } from 'components/ui/spinner';
import DropZone from 'components/Inputs/DropZone';
import { useToast } from 'hooks/useToast';
import { ApiError } from 'interfaces/api';
import { Column } from 'interfaces/column';
import { UploadedFile } from 'interfaces/file';
import { parseCsvColumns, validateCsv } from 'utils/file';
import { useService } from 'hooks/useService';
import { ContentType } from 'services/abstract';
type CsvUploaderProps = {
  onUpload: (file: UploadedFile) => void;
  onParse?: (columnNames: Column[]) => void;
  onCancel?: () => void;
  onChange: (fileId: number) => void;
};

export const CsvUploader = forwardRef<HTMLDivElement, CsvUploaderProps>(
  ({ onUpload, onParse, onCancel, onChange }, _ref) => {
    CsvUploader.displayName = 'legend';
    const { addToast } = useToast();
    const [file, setFile] = useState<UploadedFile | undefined>(undefined);
    const [errors, setErrors] = useState<string>('');
    const pageCartoService = useService(ContentType.PAGE_CARTOS);

    const { mutateAsync, isError, isLoading, error } = useMutation({
      mutationFn: async (data: { file: File }) => {
        return await pageCartoService.uploadCsv(data.file);
      },
      onSuccess: (data: UploadedFile) => {
        setFile(data);
        onUpload(data);
        onChange(data.id);
        addToast({
          title: `Fichier téléchargé`,
          description: `Fichier ${data.name} téléchargé avec succès`,
          type: 'success',
        });
      },
    });

    const handleUpload = async (csvFile: File) => {
      setErrors('');
      try {
        const data = await validateCsv(csvFile);
        if (onParse) {
          const columns = parseCsvColumns(
            data.meta.fields || [],
            data.data as Record<string, number | string>[]
          );
          onParse(columns);
        }

        if (Array.isArray(data.errors) && data.errors.length > 0) {
          // eslint-disable-next-line no-console
          console.warn(`CSV Parse errors : `, data.errors);
        }
        // This is done to set the displayed filename after the file is uploaded
        if (data.errors.length > 0) {
          setErrors(
            `Le fichier CSV contient des erreurs dans les lignes : ${data.errors
              .map((e) => e.row)
              .join(', ')}`
          );
        }
        mutateAsync({ file: csvFile });
      } catch (e) {
        addToast({
          title: `Erreur lors de la validation du fichier CSV`,
          description: (e as Error).message,
          type: 'error',
        });
      }
    };

    const handleCancel = () => {
      setFile(undefined);
      setErrors('');
      onCancel && onCancel();
    };

    if (isLoading) {
      return <Spinner />;
    }

    if (isError) {
      <ApiErrorAlert error={error as ApiError} />;
    }

    return (
      <div className="self-center">
        {file ? (
          <div
            data-testid="file-thumbnail"
            className="flex flex-col items-center justify-center pt-5 pb-6"
          >
            <BarChartIcon width={64} height={64} />
            <span className="mb-2 text-sm text-gray-500">
              {file.name} ({file.mime})
            </span>
            <Button color="red" onClick={handleCancel} size="xs">
              Annuler
            </Button>
          </div>
        ) : (
          <DropZone onUpload={handleUpload} />
        )}
        {errors && (
          <Alert className="p-2 text-sm" variant="destructive">
            <ExclamationTriangleIcon className="h-4 w-4" />
            <AlertDescription>{errors}</AlertDescription>
          </Alert>
        )}
      </div>
    );
  }
);
